/**
 * Plan Page - Kanban Board View with 3-Panel Layout
 *
 * Implements:
 * - REQ-012: Kanban Board Rendering
 * - REQ-013: Proof Tree Visualization
 */

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { usePlan } from "../contexts/PlanContext";
import Board from "../components/Board";
import DAGView from "../components/DAGView";
import TreeView from "../components/TreeView";
import SourceView from "../components/SourceView";
import ActivityView from "../components/ActivityView";
import ProofTree from "../components/ProofTree";
import CommandPalette from "../components/CommandPalette";
import SearchPalette from "../components/SearchPalette";
import TaskDetails from "../components/TaskDetails";
import TaskActions from "../components/TaskActions";
import HelpModal from "../components/HelpModal";
import RecentPlansDropdown from "../components/RecentPlansDropdown";
import { usePlanReasoning } from "../lib/usePlanReasoning";
import { getRecentPlans, updatePlanPetname } from "../lib/storage";
import { buildSearchIndex, type SearchableItem } from "../lib/fuzzySearch";
import { useResizablePanel } from "../hooks/useResizablePanel";
import ResizeHandle from "../components/ResizeHandle";
import SearchWorker from "../lib/search.worker?worker";
import type { SearchWorkerRequest, SearchWorkerResponse } from "../lib/search.worker";

type MobileTab = "board" | "activity" | "source" | "details" | "proof";
type ViewMode = "board" | "dag" | "tree";
type LeftPanelMode = "activity" | "source";

export default function Plan() {
  const { token } = useParams<{ token: string }>();
  const { entries, pendingCount, isConnected, isLoading, error, append, canAppend, scope } = usePlan();
  const [mobileTab, setMobileTab] = useState<MobileTab>("board");
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    if (typeof window !== "undefined" && token) {
      const saved = localStorage.getItem(`hence-view-${token}`);
      if (saved === "board" || saved === "dag" || saved === "tree") {
        return saved;
      }
    }
    return "board";
  });
  const [leftPanelMode, setLeftPanelMode] = useState<LeftPanelMode>("activity");
  const [selectedTask, setSelectedTask] = useState<string | null>(null);
  const [planSource, setPlanSource] = useState<string>("");
  const [planLoading, setPlanLoading] = useState(true);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [searchPaletteOpen, setSearchPaletteOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [localForms, setLocalForms] = useState<string[]>([]);
  const [showReconnecting, setShowReconnecting] = useState(false);
  const [theme, setTheme] = useState<"cupcake" | "night">(() => {
    if (typeof window !== "undefined") {
      return (localStorage.getItem("hence-theme") as "cupcake" | "night") || "cupcake";
    }
    return "cupcake";
  });
  const [petname, setPetname] = useState<string>("");
  const [isEditingPetname, setIsEditingPetname] = useState(false);
  const [petnameInput, setPetnameInput] = useState("");

  // Resizable panels
  const leftPanel = useResizablePanel({
    storageKey: "hence-left-panel-width",
    defaultSize: 384,
    minSize: 256,
    maxSize: 640,
    direction: "horizontal",
  });

  const bottomPanel = useResizablePanel({
    storageKey: "hence-bottom-panel-height",
    defaultSize: 384,
    minSize: 200,
    maxSize: 600,
    direction: "vertical",
  });

  // Apply theme to document
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("hence-theme", theme);
  }, [theme]);

  // Persist view mode per plan
  useEffect(() => {
    if (token) {
      localStorage.setItem(`hence-view-${token}`, viewMode);
    }
  }, [token, viewMode]);

  // Load petname from recent plans
  useEffect(() => {
    if (token && token !== "demo" && !token.startsWith("test-")) {
      const plans = getRecentPlans();
      const plan = plans.find((p) => p.token === token);
      if (plan?.petname) {
        setPetname(plan.petname);
      }
    }
  }, [token]);

  const handlePetnameSave = useCallback(() => {
    if (token && token !== "demo" && !token.startsWith("test-")) {
      const trimmed = petnameInput.trim();
      updatePlanPetname(token, trimmed || undefined);
      setPetname(trimmed);
    }
    setIsEditingPetname(false);
  }, [token, petnameInput]);

  const toggleTheme = useCallback(() => {
    setTheme((t) => (t === "cupcake" ? "night" : "cupcake"));
  }, []);

  // Debounce reconnecting badge - only show after 5 seconds of being disconnected
  useEffect(() => {
    if (isConnected) {
      setShowReconnecting(false);
      return;
    }
    const timeout = setTimeout(() => setShowReconnecting(true), 5000);
    return () => clearTimeout(timeout);
  }, [isConnected]);

  // Load demo plan for demo mode or test plans
  useEffect(() => {
    if (token === "demo" || token === "test-meta") {
      const planFile = token === "demo" ? "/demo-plan.spl" : "/test-meta.spl";
      fetch(planFile)
        .then((res) => res.text())
        .then((text) => {
          setPlanSource(text);
          setPlanLoading(false);
        })
        .catch((err) => {
          console.error("Failed to load plan:", err);
          setPlanLoading(false);
        });
    } else {
      setPlanLoading(false);
    }
  }, [token]);

  // Reconstruct source from entries for hosted plans
  useEffect(() => {
    if (token !== "demo" && entries.length > 0 && !planSource) {
      const forms = entries.flatMap((e) => e.forms);
      setPlanSource(forms.join("\n\n"));
    }
  }, [token, entries, planSource]);

  // Keyboard shortcuts: Cmd+K for search, Cmd+Shift+K for command palette
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        if (e.shiftKey) {
          setSearchPaletteOpen(false);
          setCommandPaletteOpen((open) => !open);
        } else {
          setCommandPaletteOpen(false);
          setSearchPaletteOpen((open) => !open);
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Convert plan source to entries format for Board (memoize to prevent infinite loops)
  const planEntries = useMemo(
    () => (planSource ? splToEntries(planSource, localForms) : []),
    [planSource, localForms]
  );
  const effectiveEntries = useMemo(
    () => (entries.length > 0 ? entries : planEntries),
    [entries, planEntries]
  );

  // Use plan reasoning for command palette (with caching based on log seq)
  const reasoning = usePlanReasoning(effectiveEntries, planSource, token);

  // Build search index in worker (off main thread)
  const [searchIndex, setSearchIndex] = useState<SearchableItem[]>([]);
  const searchWorkerRef = useRef<Worker | null>(null);

  useEffect(() => {
    const worker = new SearchWorker();
    searchWorkerRef.current = worker;

    worker.onmessage = (e: MessageEvent<SearchWorkerResponse>) => {
      if (e.data.type === "indexReady") {
        setSearchIndex(e.data.index);
      }
    };

    return () => worker.terminate();
  }, []);

  // Rebuild search index when data changes
  useEffect(() => {
    if (searchWorkerRef.current && reasoning.tasks.length > 0) {
      const taskData = reasoning.tasks.map(t => ({
        id: t.id,
        status: t.status,
        meta: t.meta,
      }));
      searchWorkerRef.current.postMessage({
        type: "buildIndex",
        entries: effectiveEntries,
        tasks: taskData,
      } as SearchWorkerRequest);
    }
  }, [effectiveEntries, reasoning.tasks]);

  const getSearchIndex = useCallback(() => searchIndex, [searchIndex]);

  // Handle appending forms (for demo mode, add locally; for real mode, use WebSocket)
  const handleAppend = useCallback(
    (forms: string[]) => {
      if (token === "demo") {
        setLocalForms((prev) => [...prev, ...forms]);
        // Also update the plan source to show new facts
        setPlanSource((prev) => prev + "\n\n; Added via command\n" + forms.join("\n"));
      } else {
        append(forms);
      }
    },
    [token, append]
  );

  // Handle task selection (must be before early returns to maintain hook order)
  const handleTaskClick = useCallback((taskId: string) => {
    setSelectedTask(taskId);
    // On mobile, switch to details tab when a task is clicked
    if (window.innerWidth < 768) {
      setMobileTab("details");
    }
  }, []);

  // Unified loading state - show single loading screen for all stages
  const showLoading = isLoading || planLoading || reasoning.isLoading;
  const loadingMessage = planLoading ? "Fetching plan..." :
    isLoading ? "Connecting..." :
    reasoning.loadingStage === "wasm" ? "Loading Spindle..." :
    reasoning.loadingStage === "reasoning" ? "Defeasibly reasoning..." : "Processing...";

  if (showLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4 bg-base-100">
        <div className="flex gap-1">
          <div className="w-3 h-3 bg-primary rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></div>
          <div className="w-3 h-3 bg-primary rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></div>
          <div className="w-3 h-3 bg-primary rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></div>
        </div>
        <span className="text-base-content/70 text-sm font-medium">
          {loadingMessage}
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="alert alert-error max-w-md">
          <span>Error: {error}</span>
        </div>
      </div>
    );
  }

  const handleComplete = (taskId: string) => {
    handleAppend([`(given completed-${taskId})`]);
  };

  const handleClaim = (taskId: string) => {
    // Add claimed fact and a defeater that defeats any existing unclaimed
    handleAppend([
      `(given claimed-${taskId})`,
      `(except d-cancel-unclaim-${taskId} claimed-${taskId} (not unclaimed-${taskId}))`
    ]);
  };

  const handleUnclaim = (taskId: string) => {
    handleAppend([
      `(given unclaimed-${taskId})`,
      `(except d-cancel-claim-${taskId} unclaimed-${taskId} (not claimed-${taskId}))`
    ]);
  };

  // Find the selected task object
  const selectedTaskObj = selectedTask
    ? reasoning.tasks.find((t) => t.id === selectedTask) || null
    : null;

  return (
    <>
      {/* Drag overlay to prevent pointer events during resize */}
      {(leftPanel.isDragging || bottomPanel.isDragging) && (
        <div
          className="fixed inset-0 z-40"
          style={{ cursor: leftPanel.isDragging ? "col-resize" : "row-resize" }}
        />
      )}

      {/* Search Palette - always mounted for instant open */}
      <SearchPalette
        isOpen={searchPaletteOpen}
        onClose={() => setSearchPaletteOpen(false)}
        getSearchIndex={getSearchIndex}
        onSelectTask={handleTaskClick}
      />

      {/* Command Palette */}
      <CommandPalette
        isOpen={commandPaletteOpen}
        onClose={() => setCommandPaletteOpen(false)}
        conclusions={reasoning.conclusions}
        tasks={reasoning.tasks}
        assignments={reasoning.assignments}
        readyTasks={reasoning.readyTasks}
        completedTasks={reasoning.completedTasks}
        blockedTasks={reasoning.blockedTasks}
        onAppend={handleAppend}
      />

      {/* Help Modal */}
      <HelpModal
        isOpen={helpOpen}
        onClose={() => setHelpOpen(false)}
        planUrl={token ? `${window.location.origin}/p/${token}` : undefined}
      />

      {/* Header */}
      <header className="flex items-center justify-between px-4 py-2 bg-base-100 border-b border-base-300">
        <div className="flex items-center gap-3">
          <Link to="/" className="text-lg font-bold">
            hence
          </Link>
          {/* Editable plan name */}
          <div className="flex items-center w-24 sm:w-32">
            {isEditingPetname ? (
              <input
                type="text"
                className="input input-xs input-bordered w-full"
                value={petnameInput}
                onChange={(e) => setPetnameInput(e.target.value)}
                onBlur={handlePetnameSave}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handlePetnameSave();
                  if (e.key === "Escape") setIsEditingPetname(false);
                }}
                autoFocus
                placeholder={token?.slice(0, 8) + "..."}
              />
            ) : (
              <button
                className="text-sm text-base-content/70 hover:text-base-content flex items-center gap-1 transition-colors truncate w-full"
                onClick={() => {
                  setPetnameInput(petname);
                  setIsEditingPetname(true);
                }}
                title="Click to set nickname"
              >
                <span className="truncate">
                  {petname || (token === "demo" ? "demo-plan.spl" : token ? `${token.slice(0, 8)}...` : "plan.spl")}
                </span>
                <svg className="w-3 h-3 opacity-50 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </button>
            )}
          </div>
          {token && token !== "demo" && !token.startsWith("test-") && (
            <span className={`badge badge-sm hidden sm:inline-flex ${
              scope === "admin" ? "badge-success" :
              scope === "append" ? "badge-info" :
              "badge-warning"
            }`}>
              {scope}
            </span>
          )}
          {/* Search Button */}
          <button
            className="btn btn-ghost btn-xs gap-1"
            onClick={() => setSearchPaletteOpen(true)}
            title="Search (⌘K)"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <span className="hidden sm:inline">Search</span>
          </button>
          {/* View Toggle */}
          <div className="join ml-2 hidden sm:flex">
            <button
              className={`join-item btn btn-xs btn-outline ${viewMode === "board" ? "btn-active" : ""}`}
              onClick={() => setViewMode("board")}
            >
              Board
            </button>
            <button
              className={`join-item btn btn-xs btn-outline ${viewMode === "dag" ? "btn-active" : ""}`}
              onClick={() => setViewMode("dag")}
            >
              DAG
            </button>
            <button
              className={`join-item btn btn-xs btn-outline ${viewMode === "tree" ? "btn-active" : ""}`}
              onClick={() => setViewMode("tree")}
            >
              Tree
            </button>
          </div>
          {showReconnecting && token !== "demo" && (
            <span className="badge badge-warning badge-sm">reconnecting</span>
          )}
          {pendingCount > 0 && (
            <span className="badge badge-info badge-sm">{pendingCount} pending</span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {/* Recent Plans */}
          <RecentPlansDropdown />
          {/* Theme Toggle */}
          <button
            className="btn btn-ghost btn-sm btn-circle"
            onClick={toggleTheme}
            title={theme === "cupcake" ? "Switch to dark mode" : "Switch to light mode"}
          >
            {theme === "cupcake" ? (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            )}
          </button>
          {/* Docs Link */}
          <a
            href="https://docs.hence.run"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-ghost btn-sm btn-circle"
            title="Documentation"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          </a>
          {/* Help Button */}
          <button
            className="btn btn-ghost btn-sm btn-circle"
            onClick={() => setHelpOpen(true)}
            title="Help"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>
        </div>
      </header>

      {/* Mobile Tabs */}
      <div className="flex bg-base-100 border-b border-base-300 md:hidden overflow-x-auto">
        {([
          { id: "board", label: "Tasks", icon: "▦" },
          { id: "activity", label: "Activity", icon: "◷" },
          { id: "source", label: "Source", icon: "{ }" },
          { id: "details", label: "Details", icon: "◉" },
          { id: "proof", label: "Proof", icon: "⊢" },
        ] as const).map((tab) => (
          <button
            key={tab.id}
            className={`flex-1 py-2.5 text-xs font-medium transition-colors ${
              mobileTab === tab.id
                ? "text-primary border-b-2 border-primary bg-primary/5"
                : "text-base-content/50"
            }`}
            onClick={() => setMobileTab(tab.id)}
          >
            <span className="block text-base mb-0.5">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Main Content */}
      <div className="flex flex-col flex-1 h-[calc(100vh-105px)] md:h-[calc(100vh-49px)]">
        {/* Top: Source + Board */}
        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* Mobile Activity Panel */}
          <div
            className={`${
              mobileTab === "activity" ? "flex" : "hidden"
            } md:hidden w-full h-full bg-base-100 flex-col overflow-hidden`}
          >
            <div className="px-4 py-2 border-b border-base-200">
              <span className="text-xs font-medium text-base-content/50 uppercase">Activity</span>
            </div>
            <div className="flex-1 overflow-auto">
              <ActivityView entries={effectiveEntries} onTaskClick={handleTaskClick} />
            </div>
          </div>

          {/* Desktop: Left Activity/Source Panel */}
          <div
            className="hidden md:flex border-r border-base-300 bg-base-100 flex-col shrink-0 min-h-0 overflow-hidden relative"
            style={{ width: leftPanel.size }}
          >
            <ResizeHandle
              direction="horizontal"
              onMouseDown={leftPanel.handleMouseDown}
              isDragging={leftPanel.isDragging}
            />
            {/* Panel tabs */}
            <div className="flex border-b border-base-200 shrink-0">
              <button
                className={`flex-1 px-3 py-2 text-xs font-medium transition-colors ${
                  leftPanelMode === "activity"
                    ? "text-primary border-b-2 border-primary"
                    : "text-base-content/50 hover:text-base-content/70"
                }`}
                onClick={() => setLeftPanelMode("activity")}
              >
                Activity
              </button>
              <button
                className={`flex-1 px-3 py-2 text-xs font-medium transition-colors ${
                  leftPanelMode === "source"
                    ? "text-primary border-b-2 border-primary"
                    : "text-base-content/50 hover:text-base-content/70"
                }`}
                onClick={() => setLeftPanelMode("source")}
              >
                Source
              </button>
            </div>
            {/* Panel content */}
            <div className="flex-1 min-h-0 overflow-auto">
              {leftPanelMode === "activity" ? (
                <ActivityView entries={effectiveEntries} onTaskClick={handleTaskClick} />
              ) : (
                <div className="p-3 code-editor">
                  <SourceView source={planSource} onLineClick={() => {}} />
                </div>
              )}
            </div>
          </div>

          {/* Right: Board, DAG, or Tree */}
          <div
            className={`${
              mobileTab === "board" ? "flex" : "hidden"
            } md:flex flex-1 flex-col min-w-0 overflow-hidden`}
          >
            {/* Mobile view toggle */}
            <div className="flex bg-base-100 border-b border-base-300 md:hidden">
              {([
                { id: "board", label: "Board" },
                { id: "dag", label: "DAG" },
                { id: "tree", label: "Tree" },
              ] as const).map((view) => (
                <button
                  key={view.id}
                  className={`flex-1 py-2 text-xs font-medium transition-colors ${
                    viewMode === view.id
                      ? "text-primary border-b-2 border-primary bg-primary/5"
                      : "text-base-content/50"
                  }`}
                  onClick={() => setViewMode(view.id)}
                >
                  {view.label}
                </button>
              ))}
            </div>
            {viewMode === "board" ? (
              <div className="flex-1 overflow-auto p-2 sm:p-4">
                <Board
                  tasks={reasoning.tasks}
                  isLoading={reasoning.isLoading}
                  onTaskClick={handleTaskClick}
                  selectedTask={selectedTask}
                />
              </div>
            ) : viewMode === "dag" ? (
              <DAGView
                tasks={reasoning.tasks}
                taskDeps={reasoning.taskDeps}
                isLoading={reasoning.isLoading}
                onTaskClick={handleTaskClick}
                selectedTask={selectedTask}
              />
            ) : (
              <TreeView
                tasks={reasoning.tasks}
                taskDeps={reasoning.taskDeps}
                isLoading={reasoning.isLoading}
                onTaskClick={handleTaskClick}
                selectedTask={selectedTask}
              />
            )}
            {/* Shared stats bar for all views */}
            {!reasoning.isLoading && reasoning.tasks.length > 0 && (
              <div className="flex items-center gap-4 px-4 py-2 bg-base-100 shadow-[inset_0_0_0_100vmax_rgba(0,0,0,0.03)] border-t border-base-300 font-mono text-sm text-base-content/70 shrink-0">
                <span className="font-semibold text-base-content">
                  {reasoning.tasks.length} task{reasoning.tasks.length !== 1 ? "s" : ""}
                </span>
                <span className="border-l border-base-300 h-4"></span>
                <span className="text-success">{reasoning.tasks.filter(t => t.status === "done").length} done</span>
                <span className="text-primary">{reasoning.tasks.filter(t => t.status === "ready").length} ready</span>
                <span className="text-secondary">{reasoning.tasks.filter(t => t.status === "in_progress").length} in-progress</span>
                <span className="text-warning">{reasoning.tasks.filter(t => t.status === "blocked").length} blocked</span>
                <span className="text-base-content/50">{reasoning.tasks.filter(t => t.status === "backlog").length} backlog</span>
              </div>
            )}
          </div>
        </div>

        {/* Bottom: Proof Tree + Task Details (fixed to bottom, full width) */}
        <div
          className="hidden md:flex border-t border-base-300 bg-base-100 shrink-0 relative"
          style={{ height: bottomPanel.size }}
        >
          <ResizeHandle
            direction="vertical"
            onMouseDown={bottomPanel.handleMouseDown}
            isDragging={bottomPanel.isDragging}
          />
          {/* Proof Tree */}
          <div className="flex-1 flex flex-col border-r border-base-200 basis-0">
            <div className="px-4 py-2 border-b border-base-200 flex justify-between items-center">
              <span className="text-xs font-medium text-base-content/50 uppercase">Proof Tree</span>
              <span className="text-xs">
                {selectedTask ? (
                  <span className="text-success">+d proven</span>
                ) : (
                  "click a task"
                )}
              </span>
            </div>
            <div className="p-4 overflow-auto flex-1 spl-code">
              {selectedTask ? (
                <ProofTree task={selectedTask} entries={effectiveEntries} />
              ) : (
                <div className="text-base-content/40 italic">
                  Select a task to view its derivation
                </div>
              )}
            </div>
          </div>

          {/* Task Details */}
          <div className="flex-1 flex flex-col basis-0 border-r border-base-200">
            <div className="px-4 py-2 border-b border-base-200 flex justify-between items-center">
              <span className="text-xs font-medium text-base-content/50 uppercase">Task Details</span>
            </div>
            <div className="p-4 overflow-auto flex-1">
              {selectedTaskObj ? (
                <TaskDetails task={selectedTaskObj} />
              ) : (
                <div className="text-base-content/40 italic text-sm">
                  Select a task to view details
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex-1 flex flex-col basis-0">
            <div className="px-4 py-2 border-b border-base-200 flex justify-between items-center">
              <span className="text-xs font-medium text-base-content/50 uppercase">Actions</span>
            </div>
            <div className="p-4 overflow-auto flex-1">
              {selectedTaskObj ? (
                <TaskActions
                  task={selectedTaskObj}
                  canAppend={canAppend}
                  onComplete={handleComplete}
                  onClaim={handleClaim}
                  onUnclaim={handleUnclaim}
                />
              ) : (
                <div className="text-base-content/40 italic text-sm">
                  Select a task to see actions
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Source Panel */}
        <div
          className={`${
            mobileTab === "source" ? "flex" : "hidden"
          } md:hidden w-full h-full bg-base-100 flex-col overflow-hidden`}
        >
          <div className="px-4 py-2 border-b border-base-200">
            <span className="text-xs font-medium text-base-content/50 uppercase">Source</span>
          </div>
          <div className="flex-1 overflow-auto p-3 code-editor">
            <SourceView source={planSource} onLineClick={() => {}} />
          </div>
        </div>

        {/* Mobile Details Panel */}
        <div
          className={`${
            mobileTab === "details" ? "flex" : "hidden"
          } md:hidden w-full h-full bg-base-100 flex-col overflow-hidden`}
        >
          {selectedTaskObj ? (
            <div className="flex-1 overflow-auto">
              {/* Task header */}
              <div className="px-4 py-3 border-b border-base-200 bg-base-100 sticky top-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono font-bold text-lg">{selectedTaskObj.id}</span>
                  <span className={`badge badge-sm ${
                    selectedTaskObj.status === "done" ? "badge-success" :
                    selectedTaskObj.status === "in_progress" ? "badge-secondary" :
                    selectedTaskObj.status === "ready" ? "badge-primary" :
                    selectedTaskObj.status === "blocked" ? "badge-warning" : "badge-ghost"
                  }`}>
                    {selectedTaskObj.status.replace("_", " ")}
                  </span>
                </div>
              </div>

              {/* Task details */}
              <div className="p-4 space-y-4">
                <TaskDetails task={selectedTaskObj} hideHeader />

                <div className="border-t border-base-200 pt-4">
                  <TaskActions
                    task={selectedTaskObj}
                    canAppend={canAppend}
                    onComplete={handleComplete}
                    onClaim={handleClaim}
                    onUnclaim={handleUnclaim}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center text-base-content/40 italic p-4">
              Select a task from the Board to view details
            </div>
          )}
        </div>

        {/* Mobile Proof Panel */}
        <div
          className={`${
            mobileTab === "proof" ? "flex" : "hidden"
          } md:hidden w-full h-full bg-base-100 flex-col`}
        >
          <div className="px-4 py-2 border-b border-base-200 flex justify-between items-center">
            <span className="text-xs font-medium text-base-content/50 uppercase">
              Proof Tree
            </span>
            <span className="text-xs">
              {selectedTask ? (
                <span className="text-success">+d proven</span>
              ) : (
                "click a task"
              )}
            </span>
          </div>
          <div className="p-4 overflow-auto flex-1 spl-code">
            {selectedTask ? (
              <ProofTree task={selectedTask} entries={effectiveEntries} />
            ) : (
              <div className="text-base-content/40 italic">
                Select a task to view its derivation
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * Parse SPL source into complete S-expressions.
 * Properly handles multi-line forms by tracking parenthesis depth.
 */
function parseSplForms(spl: string): string[] {
  const forms: string[] = [];
  let current = "";
  let depth = 0;
  let inString = false;
  let escaped = false;

  for (let i = 0; i < spl.length; i++) {
    const char = spl[i];

    // Handle escape sequences in strings
    if (escaped) {
      current += char;
      escaped = false;
      continue;
    }

    if (char === "\\" && inString) {
      current += char;
      escaped = true;
      continue;
    }

    // Handle string boundaries
    if (char === '"') {
      current += char;
      inString = !inString;
      continue;
    }

    // Skip characters inside strings
    if (inString) {
      current += char;
      continue;
    }

    // Handle comments (skip to end of line)
    if (char === ";") {
      // Skip to end of line
      while (i < spl.length && spl[i] !== "\n") {
        i++;
      }
      // If we're in the middle of a form, add a space to preserve separation
      if (depth > 0) {
        current += " ";
      }
      continue;
    }

    // Handle parentheses
    if (char === "(") {
      if (depth === 0) {
        current = "(";
      } else {
        current += char;
      }
      depth++;
      continue;
    }

    if (char === ")") {
      depth--;
      current += char;
      if (depth === 0 && current.trim()) {
        // Normalize whitespace in the completed form
        forms.push(current.replace(/\s+/g, " ").trim());
        current = "";
      }
      continue;
    }

    // Accumulate other characters when inside a form
    if (depth > 0) {
      current += char;
    }
  }

  return forms;
}

/**
 * Convert SPL source to entries format
 */
function splToEntries(
  spl: string,
  additionalForms: string[] = []
): Array<{
  seq: number;
  ts: string;
  agent: string;
  forms: string[];
  sig: string;
}> {
  // Parse complete S-expressions from the source
  const forms = parseSplForms(spl);

  // Add any additional forms
  forms.push(...additionalForms);

  return [
    {
      seq: 1,
      ts: new Date().toISOString(),
      agent: "system",
      forms,
      sig: "plan-source",
    },
  ];
}
